import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import GeneralTable from "../../../components/Table/GeneralTable";
import "./Allowances.scss";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import IconButton from "../../../components/IconButton/IconButton";

function Allowances() {
  const [allowances, setAllowances] = useState([]);
  const [newAllowance, setNewAllowance] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingAllowance, setEditingAllowance] = useState(null);

  useEffect(() => {
    fetchAllowances();
  }, []);

  const fetchAllowances = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/allowances`,
      );
      const { data } = response.data;
      setAllowances(data || []);
    } catch (err) {
      console.error("Error fetching allowances:", err);
      setError("Failed to fetch allowances");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAllowance = async (e) => {
    e.preventDefault();
    if (!newAllowance.trim()) {
      setError("Allowance name cannot be empty");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = { otherAllowanceId: 0, allowanceName: newAllowance };
      await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL}/allowances`,
        payload,
      );
      setNewAllowance("");
      fetchAllowances(); // Re-fetch allowances after creating a new one
    } catch (err) {
      console.error("Error creating allowance:", err);
      setError("Failed to create allowance");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAllowance = (allowance) => {
    setEditingAllowance(allowance);
  };

  const handleSaveEdit = async () => {
    if (!editingAllowance?.allowanceName?.trim()) {
      setError("Allowance name cannot be empty");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        otherAllowanceId: editingAllowance.otherAllowanceId,
        allowanceName: editingAllowance.allowanceName,
      };
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/allowances`,
        payload,
      );
      setEditingAllowance(null);
      fetchAllowances(); // Re-fetch allowances after editing one
    } catch (err) {
      console.error("Error editing allowance:", err);
      setError("Failed to edit allowance");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAllowance(null);
  };

  // Define table columns
  const tableColumns = [
    { header: "Name", accessor: "allowanceName" },
    { header: "Actions", accessor: "actions" },
  ];

  // Transform data for the GeneralTable
  const enhancedAllowances = allowances.map((allowance) => ({
    ...allowance,
    allowanceName:
      editingAllowance?.otherAllowanceId === allowance.otherAllowanceId ? (
        <input
          type="text"
          value={editingAllowance.allowanceName || ""}
          onChange={(e) =>
            setEditingAllowance({
              ...editingAllowance,
              allowanceName: e.target.value,
            })
          }
          className="table-input"
          placeholder="Enter allowance name"
        />
      ) : (
        allowance.allowanceName || "Unnamed Allowance"
      ),
    actions: (
      <div className="action-buttons">
        {editingAllowance?.otherAllowanceId === allowance.otherAllowanceId ? (
          <>
            <IconButton
              className="icon-button--add"
              onClick={handleSaveEdit}
              icon="material-symbols-light:add"
              disabled={loading}
            />
            <IconButton
              className="icon-button--delete"
              onClick={handleCancelEdit}
              icon="material-symbols-light:cancel"
            />
          </>
        ) : (
          <IconButton
            className="icon-button--edit"
            onClick={() => handleEditAllowance(allowance)}
            icon="iconamoon:edit-light"
            disabled={loading}
            title="Edit Allowance"
          />
        )}
      </div>
    ),
  }));

  return (
    <div className="allowances-container">
      <h1 className="display6">Allowance Management</h1>

      {loading && <LoadingScreen />}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Create Allowance Form */}
      <div className="form-container">
        <form onSubmit={handleCreateAllowance}>
          <div className="form-group">
            <label htmlFor="newAllowance">New Allowance Name:</label>
            <div className="input-group">
              <input
                type="text"
                id="newAllowance"
                value={newAllowance}
                onChange={(e) => setNewAllowance(e.target.value)}
                placeholder="Enter allowance name"
                required
                disabled={loading}
              />
              <button type="submit" disabled={loading} className="add-button">
                {loading ? "Submitting..." : "Add"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Use GeneralTable instead of custom table */}
      <GeneralTable
        columns={tableColumns}
        data={enhancedAllowances}
        title="Existing Allowances"
      />
    </div>
  );
}

export default Allowances;
